import React, { useEffect, useMemo, useState } from 'react';
import { Card, DatePicker, Table, InputNumber, Button, Space, message, Statistic, Row, Col, Alert, Upload, Typography, Input, Modal, Form, Select, Popconfirm, Tag } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import api from '../../api/http';

// 安全积分汇算页面（作为 Tab 内容组件使用）
// 设计要点：
// 1. 月份选择 + 奖金总额(total_bonus) + 基础系数(base_multiplier)
// 2. 加载员工基础列表（具备 view_employees 权限）并允许填写/编辑各项系数；加载已存在记录则覆盖
// 3. 校验：所有数值 >= 0；info_rate >= 0（不再是百分比，直接作为乘数）；base_multiplier > 0
// 4. 提交后展示后端返回的 issued_amount、composite_coeff 等最终结果
// 5. 只要有 safety_points_view 就能看；有 safety_points_manage 时可编辑与计算
// 6. 支持“重新加载已保存记录”与“清空修改回退至员工初始”

const SafetyPointsCalcPage = ({ permissions }) => {
  const canManage = permissions.includes('safety_points_manage');
  const canView = canManage || permissions.includes('safety_points_view');
  const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
  const [nameQuery, setNameQuery] = useState('');
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]); // 原始员工列表
  const [rows, setRows] = useState([]); // 可编辑/计算行（可能包含已存在记录）
  // totalBonus 旧模式已不再参与新公式，保留变量以兼容历史记录加载
  const [totalBonus, setTotalBonus] = useState(null);
  const [baseMultiplier, setBaseMultiplier] = useState(1);
  const [compositeTotal, setCompositeTotal] = useState(0);
  const [calcMeta, setCalcMeta] = useState(null); // 后端返回的汇总
  // 已移除权重公式，保留基础系数
  const [importing, setImporting] = useState(false);
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [importPreviewLoading, setImportPreviewLoading] = useState(false);
  const [importPreviewFile, setImportPreviewFile] = useState(null);
  const [importPreviewRows, setImportPreviewRows] = useState([]);
  const [importPreviewStats, setImportPreviewStats] = useState(null);
  const [importBindings, setImportBindings] = useState({}); // row_no -> employee_id
  const [internalAssessItems, setInternalAssessItems] = useState([]);
  const [internalAssessItemsLoading, setInternalAssessItemsLoading] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  // 汇总统计
  const [issuedTotal, setIssuedTotal] = useState(0);
  const [responsibilityTotal, setResponsibilityTotal] = useState(0);
  const [diffToTotalBonus, setDiffToTotalBonus] = useState(null);
  // 折叠公式说明
  const [showFormula, setShowFormula] = useState(false);

  // 加减分弹窗
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustEmployee, setAdjustEmployee] = useState(null);
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [adjustSaving, setAdjustSaving] = useState(false);
  const [adjustRecords, setAdjustRecords] = useState([]);
  const [adjustForm] = Form.useForm();

  // 初始化加载员工（仅一次）
  useEffect(() => {
    if (!canView) return;
    const loadEmployees = async () => {
      setLoadingEmployees(true);
      try {
        const res = await api.get('/employees');
        const now = dayjs();
  const base = (res.data || []).map(emp => {
          // 角色基础分映射
            let baseSec;
            const pos = (emp.position || '').trim();
            if (pos === '项目经理') baseSec = 9;
            else if (pos === '副经理') baseSec = 7;
            else if (pos === '安全员') baseSec = 7;
            else baseSec = 3;
            // 试用期 <3 个月覆盖为 2.4
            if (emp.hire_date) {
              const months = now.diff(dayjs(emp.hire_date), 'month', true);
              if (!isNaN(months) && months < 3) baseSec = 2.4;
            }
            const baseTeam = 1; // 固定默认 1
            const actualSec = parseFloat((baseSec * baseTeam).toFixed(4));
            const finalSafety = parseFloat((1 * 100).toFixed(4)); // 初始 info_rate=1 => 安全分=100
            const compositeCoeff = parseFloat((actualSec * (finalSafety + 0)).toFixed(6));
            return {
              employee_id: emp.id,
              name: emp.name,
              department: emp.department || emp.section || '',
              unit_name: emp.section || '',
              position: emp.position || '',
              base_security_score: baseSec,
              base_team_score: baseTeam,
              actual_security_score: actualSec,
              info_rate: 1, // 直接乘数，不再按百分比
              company_assess1_amount: 0,
              company_assess2_amount: 0,
              internal_assess_amount: 0,
              mobile_assess_amount: 0,
              responsibility_bonus: 0,
              zero_injury_bonus: 0,
              attendance_days: 0,
              // 计算后字段
              final_safety_score: finalSafety,
              composite_coeff: compositeCoeff,
              issued_amount: null,
              assessment_amount: null
            };
        });
        setEmployees(base);
        setRows(base);
      } catch (e) {
        message.error('加载员工失败: ' + (e.response?.data?.error || e.message));
      } finally {
        setLoadingEmployees(false);
      }
    };
    loadEmployees();
  }, [canView]);

  const recomputeRowWithMultiplier = (row, multiplier, explicitAdjustmentPoints) => {
    const baseSecNow = parseFloat(row.base_security_score) || 0;
    const baseTeamNow = parseFloat(row.base_team_score) || 0;
    const actualSecNow = parseFloat((baseSecNow * baseTeamNow).toFixed(4));
    const infoRateNow = parseFloat(row.info_rate) || 0;
    const companyA1Now = parseFloat(row.company_assess1_amount) || 0;
    const companyA2Now = parseFloat(row.company_assess2_amount) || 0;
    const internalANow = parseFloat(row.internal_assess_amount) || 0;
    const mobileANow = parseFloat(row.mobile_assess_amount) || 0;
    const zeroInjuryNow = parseFloat(row.zero_injury_bonus) || 0;
    const adj = explicitAdjustmentPoints !== undefined ? (parseFloat(explicitAdjustmentPoints) || 0) : (parseFloat(row.adjustment_points) || 0);

    const finalSafety = infoRateNow * 100 + adj;
    const compositeCoeff = actualSecNow * (finalSafety + companyA1Now);
    const responsibilityBonus = compositeCoeff * (parseFloat(multiplier) || 0) + companyA2Now + internalANow + mobileANow;
    const issuedAmount = responsibilityBonus + zeroInjuryNow;
    // 考核汇总金额不包含“厂内部考核金额”（厂内部考核金额单独字段 internal_assess_amount）
    const assessmentAmount = companyA1Now + companyA2Now + mobileANow;

    return {
      ...row,
      actual_security_score: actualSecNow,
      adjustment_points: adj,
      final_safety_score: parseFloat(finalSafety.toFixed(4)),
      composite_coeff: parseFloat(compositeCoeff.toFixed(6)),
      responsibility_bonus: parseFloat(responsibilityBonus.toFixed(2)),
      issued_amount: parseFloat(issuedAmount.toFixed(2)),
      assessment_amount: parseFloat(assessmentAmount.toFixed(2))
    };
  };

  const loadAdjustmentSummary = async () => {
    if (!month) return;
    try {
      const res = await api.get('/safety-points/adjustments/summary', { params: { month } });
      const map = new Map();
      (res.data?.list || []).forEach(it => map.set(Number(it.employee_id), Number(it.adjustment_points) || 0));
      setRows(prev => prev.map(r => recomputeRowWithMultiplier(r, baseMultiplier, map.get(Number(r.employee_id)) || 0)));
    } catch (e) {
      // 静默失败：不阻塞主流程
    }
  };

  useEffect(() => {
    if (!canView) return;
    loadAdjustmentSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, canView]);

  const loadAdjustmentsForEmployee = async (employeeId) => {
    setAdjustLoading(true);
    try {
      const res = await api.get('/safety-points/adjustments', { params: { month, employee_id: employeeId } });
      setAdjustRecords(res.data?.records || []);
    } catch (e) {
      message.error(e.response?.data?.error || e.message || '加载加减分流水失败');
      setAdjustRecords([]);
    } finally {
      setAdjustLoading(false);
    }
  };

  const openAdjustModal = async (row) => {
    setAdjustEmployee(row);
    setAdjustOpen(true);
    adjustForm.resetFields();
    adjustForm.setFieldsValue({
      action: 'add',
      category: 'behavior',
      points: 1,
      occurred_date: dayjs(),
      description: ''
    });
    await loadAdjustmentsForEmployee(row.employee_id);
  };

  const submitAdjustment = async () => {
    if (!adjustEmployee) return;
    setAdjustSaving(true);
    try {
      const v = await adjustForm.validateFields();
      const payload = {
        month,
        employee_id: adjustEmployee.employee_id,
        action: v.action,
        category: v.category,
        points: v.points,
        occurred_date: v.occurred_date ? v.occurred_date.format('YYYY-MM-DD') : undefined,
        description: v.description || null
      };
      await api.post('/safety-points/adjustments', payload);
      message.success('已保存加减分');
      await loadAdjustmentsForEmployee(adjustEmployee.employee_id);
      await loadAdjustmentSummary();
    } catch (e) {
      if (!e?.errorFields) message.error(e.response?.data?.error || e.message || '保存失败');
    } finally {
      setAdjustSaving(false);
    }
  };

  const deleteAdjustment = async (id) => {
    if (!adjustEmployee) return;
    try {
      await api.delete('/safety-points/adjustments/' + id);
      message.success('已删除');
      await loadAdjustmentsForEmployee(adjustEmployee.employee_id);
      await loadAdjustmentSummary();
    } catch (e) {
      message.error(e.response?.data?.error || e.message || '删除失败');
    }
  };

  const loadSavedRecords = async () => {
    if (!month) return message.warning('请选择月份');
    setLoadingRecords(true);
    try {
      const res = await api.get('/safety-points/records', { params: { month } });
      const map = new Map();
      rows.forEach(r => map.set(r.employee_id, r));
      // 覆盖已有记录
      (res.data.records || []).forEach(rec => {
        const existing = map.get(rec.employee_id);
        const merged = {
          employee_id: rec.employee_id,
          name: existing?.name || '',
          department: rec.department || existing?.department || '',
          unit_name: rec.unit_name || existing?.unit_name || '',
          position: rec.position || existing?.position || '',
          base_security_score: parseFloat(rec.base_security_score)||0,
          base_team_score: parseFloat(rec.base_team_score)||0,
          actual_security_score: parseFloat(rec.actual_security_score)||0,
          info_rate: parseFloat(rec.info_rate)||0,
          company_assess1_amount: parseFloat(rec.company_assess1_amount)||0,
          company_assess2_amount: parseFloat(rec.company_assess2_amount)||0,
          internal_assess_amount: parseFloat(rec.internal_assess_amount)||0,
          mobile_assess_amount: parseFloat(rec.mobile_assess_amount)||0,
          responsibility_bonus: parseFloat(rec.responsibility_bonus)||0,
          zero_injury_bonus: parseFloat(rec.zero_injury_bonus)||0,
          attendance_days: parseInt(rec.attendance_days)||0,
          final_safety_score: parseFloat(rec.final_safety_score)||null,
          composite_coeff: parseFloat(rec.composite_coeff)||null,
          issued_amount: parseFloat(rec.issued_amount)||null,
          assessment_amount: parseFloat(rec.assessment_amount)||null
        };
        map.set(rec.employee_id, merged);
      });
      const mergedRows = Array.from(map.values());
      setRows(mergedRows);
      // 后端记录包含 total_bonus_input/base_multiplier，更新界面
      if (res.data.records && res.data.records.length) {
        setTotalBonus(parseFloat(res.data.records[0].total_bonus_input)||null);
        setBaseMultiplier(parseFloat(res.data.records[0].base_multiplier)||1);
      }
      message.success('已加载已保存记录');
      // 同步刷新：导入明细汇总(项目文本 + 厂内部考核金额)
      loadInternalAssessItems();
    } catch(e){
      message.error('加载已保存记录失败');
    } finally { setLoadingRecords(false); }
  };

  const resetToEmployeeBase = () => {
    setRows(employees);
    setCompositeTotal(0);
    setCalcMeta(null);
  };

  // 新公式不再要求内部考核金额求和为 0，移除该校验

  const updateRow = (employee_id, field, value) => {
    setRows(prev => prev.map(r => {
      if(r.employee_id !== employee_id) return r;
      const newVal = value==null?0:value;
      let updated = { ...r, [field]: newVal };
      // 重新计算实际安全分
      if(field === 'base_security_score' || field === 'base_team_score') {
        const baseSec = field==='base_security_score'? newVal : (parseFloat(r.base_security_score)||0);
        const baseTeam = field==='base_team_score'? newVal : (parseFloat(r.base_team_score)||0);
        updated.actual_security_score = parseFloat((baseSec * baseTeam).toFixed(4));
      }
      // 即时前端预计算（与后端一致）：
      const baseSecNow = parseFloat(updated.base_security_score)||0;
      const baseTeamNow = parseFloat(updated.base_team_score)||0;
      const actualSecNow = parseFloat((baseSecNow * baseTeamNow).toFixed(4));
      updated.actual_security_score = actualSecNow;
      const infoRateNow = parseFloat(updated.info_rate)||0;
      const companyA1Now = parseFloat(updated.company_assess1_amount)||0;
      const companyA2Now = parseFloat(updated.company_assess2_amount)||0;
      const internalANow = parseFloat(updated.internal_assess_amount)||0;
      const mobileANow = parseFloat(updated.mobile_assess_amount)||0;
      const zeroInjuryNow = parseFloat(updated.zero_injury_bonus)||0;
      const adjPoints = parseFloat(updated.adjustment_points) || 0;
      const finalSafety = infoRateNow * 100 + adjPoints; // 新公式：安全分 = 信息化履职率×100 + 月度加减分
      const compositeCoeff = actualSecNow * (finalSafety + companyA1Now);
      updated.final_safety_score = parseFloat(finalSafety.toFixed(4));
      updated.composite_coeff = parseFloat(compositeCoeff.toFixed(6));
      const responsibilityBonus = compositeCoeff * (parseFloat(baseMultiplier)||0) + companyA2Now + internalANow + mobileANow;
      const issuedAmount = responsibilityBonus + zeroInjuryNow;
      // 考核汇总金额不包含“厂内部考核金额”（厂内部考核金额单独字段 internal_assess_amount）
      const assessmentAmount = companyA1Now + companyA2Now + mobileANow;
      updated.responsibility_bonus = parseFloat(responsibilityBonus.toFixed(2));
      updated.issued_amount = parseFloat(issuedAmount.toFixed(2));
      updated.assessment_amount = parseFloat(assessmentAmount.toFixed(2));
      return updated;
    }));
  };

  // 基础系数变化时批量刷新奖金相关预估字段
  useEffect(()=>{
    setRows(prev => prev.map(r => {
      const actualSecNow = parseFloat(r.actual_security_score)||0;
      const infoRateNow = parseFloat(r.info_rate)||0;
      const adjPoints = parseFloat(r.adjustment_points)||0;
      const finalSafety = (parseFloat(r.final_safety_score)!=null ? parseFloat(r.final_safety_score) : infoRateNow * 100 + adjPoints);
      const companyA1 = parseFloat(r.company_assess1_amount)||0;
      const companyA2 = parseFloat(r.company_assess2_amount)||0;
      const internalA = parseFloat(r.internal_assess_amount)||0;
      const mobileA = parseFloat(r.mobile_assess_amount)||0;
      const zeroInjury = parseFloat(r.zero_injury_bonus)||0;
      const compositeCoeff = actualSecNow * (finalSafety + companyA1);
      const responsibilityBonus = compositeCoeff * (parseFloat(baseMultiplier)||0) + companyA2 + internalA + mobileA;
      const issuedAmount = responsibilityBonus + zeroInjury;
      // 考核汇总金额不包含“厂内部考核金额”（厂内部考核金额单独字段 internal_assess_amount）
      const assessmentAmount = companyA1 + companyA2 + mobileA;
      return {
        ...r,
        composite_coeff: parseFloat(compositeCoeff.toFixed(6)),
        responsibility_bonus: parseFloat(responsibilityBonus.toFixed(2)),
        issued_amount: parseFloat(issuedAmount.toFixed(2)),
        assessment_amount: parseFloat(assessmentAmount.toFixed(2))
      };
    }));
  }, [baseMultiplier]);

  const validateBeforeCalc = () => {
    if (!month) { message.error('请选择月份'); return false; }
    if (baseMultiplier == null || baseMultiplier <= 0) { message.error('请输入有效的基础系数'); return false; }
    for (const r of rows) {
      if (r.info_rate < 0) { message.error(`信息化履职率不能为负数 (员工ID ${r.employee_id})`); return false; }
      const fields = ['base_security_score','base_team_score','actual_security_score','company_assess1_amount','company_assess2_amount','mobile_assess_amount','zero_injury_bonus'];
      for (const f of fields) {
        if (r[f] < 0) { message.error(`${f} 不能为负数 (员工ID ${r.employee_id})`); return false; }
      }
    }
    return true;
  };

  const performCalculation = async () => {
    if (!validateBeforeCalc()) return;
    setSaving(true);
    try {
      const payloadItems = rows.map(r => ({
        employee_id: r.employee_id,
        department: r.department,
        unit_name: r.unit_name,
        position: r.position,
        base_security_score: r.base_security_score,
        base_team_score: r.base_team_score,
        actual_security_score: r.actual_security_score,
        info_rate: r.info_rate,
        company_assess1_amount: r.company_assess1_amount,
        company_assess2_amount: r.company_assess2_amount,
        internal_assess_amount: r.internal_assess_amount,
        mobile_assess_amount: r.mobile_assess_amount,
        responsibility_bonus: r.responsibility_bonus,
        zero_injury_bonus: r.zero_injury_bonus,
        attendance_days: r.attendance_days
      }));
      const res = await api.post('/safety-points/calculate', {
        month,
        base_multiplier: baseMultiplier,
        total_bonus: totalBonus,
        items: payloadItems
      });
      const updated = (res.data.records || []).map(rec => ({
        employee_id: rec.employee_id,
        name: rows.find(r=>r.employee_id===rec.employee_id)?.name || '',
        department: rec.department || '',
        unit_name: rec.unit_name || '',
        position: rec.position || '',
        base_security_score: parseFloat(rec.base_security_score)||0,
        base_team_score: parseFloat(rec.base_team_score)||0,
        actual_security_score: parseFloat(rec.actual_security_score)||0,
        info_rate: parseFloat(rec.info_rate)||0,
        company_assess1_amount: parseFloat(rec.company_assess1_amount)||0,
        company_assess2_amount: parseFloat(rec.company_assess2_amount)||0,
        internal_assess_amount: parseFloat(rec.internal_assess_amount)||0,
        mobile_assess_amount: parseFloat(rec.mobile_assess_amount)||0,
        responsibility_bonus: parseFloat(rec.responsibility_bonus)||0,
        zero_injury_bonus: parseFloat(rec.zero_injury_bonus)||0,
        attendance_days: parseInt(rec.attendance_days)||0,
        final_safety_score: parseFloat(rec.final_safety_score)||null,
        composite_coeff: parseFloat(rec.composite_coeff)||null,
        issued_amount: parseFloat(rec.issued_amount)||null,
        assessment_amount: parseFloat(rec.assessment_amount)||null
      }));
      setRows(updated);
  setCompositeTotal(parseFloat(res.data.composite_total)||0);
  if(res.data.total_bonus_input!=null) setTotalBonus(res.data.total_bonus_input);
    setCalcMeta({ base_multiplier: res.data.base_multiplier });
      message.success('汇算完成');
    } catch(e){
      message.error('汇算失败: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  const exportCalcExcel = () => {
    const exportRows = filteredRows || rows || [];
    if(!exportRows.length){ message.warning('没有可导出的数据'); return; }
    const data = exportRows.map((r, idx) => ({
      '序号': idx + 1,
      '姓名': r.name,
      '部门': r.department,
      '科室/单位': r.unit_name,
      '岗位': r.position,
      '安全治安消防基础分': Number(r.base_security_score || 0),
      '班组奖惩基础分': Number(r.base_team_score || 0),
      '消防系数实际分值': Number(r.actual_security_score || 0),
      '信息化履职率': Number(r.info_rate || 0),
      '加减分': Number(r.adjustment_points || 0),
      '安全分': r.final_safety_score==null ? '' : Number(r.final_safety_score),
      '安全奖综合系数': r.composite_coeff==null ? '' : Number(r.composite_coeff),
      '公司考核1金额': Number(r.company_assess1_amount || 0),
      '公司考核2金额': Number(r.company_assess2_amount || 0),
      '厂内部考核金额': Number(r.internal_assess_amount || 0),
      '机动考评金额': Number(r.mobile_assess_amount || 0),
      '责任制奖金': r.responsibility_bonus==null ? '' : Number(r.responsibility_bonus),
      '百日零伤害奖励': Number(r.zero_injury_bonus || 0),
      '出勤天数': Number(r.attendance_days || 0),
      '核发金额': r.issued_amount==null ? '' : Number(r.issued_amount),
      '考核汇总金额': r.assessment_amount==null ? '' : Number(r.assessment_amount),
      '考核项目(扣/奖)': r.assessment_items_text || ''
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '汇算结果');
    const fileName = `安全积分汇算_${month}_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const exportInternalAssessExcel = () => {
    const list = internalAssessItems || [];
    if(!list.length){ message.warning('没有可导出的导入明细'); return; }
    const data = list.map((r) => ({
      '序号': r.seq_no ?? '',
      '姓名': r.name || '',
      '扣款项目': r.deduct_item || '',
      '扣款（元）': Number(r.deduct_amount || 0),
      '扣款类型': r.deduct_type || '',
      '奖励项目': r.reward_item || '',
      '奖励（元）': Number(r.reward_amount || 0),
      '奖励类型': r.reward_type || '',
      '上月累计项目': r.last_month_cumulative_item || '',
      '个人小计': Number(r.personal_subtotal || 0)
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '导入明细');
    const fileName = `厂内部考核奖励明细_${month}_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // 汇总统计（前端动态）
  useEffect(() => {
    let issuedSum = 0;
    let respSum = 0;
    rows.forEach(r => {
      if (typeof r.issued_amount === 'number') issuedSum += r.issued_amount;
      if (typeof r.responsibility_bonus === 'number') respSum += r.responsibility_bonus;
    });
    setIssuedTotal(parseFloat(issuedSum.toFixed(2)));
    setResponsibilityTotal(parseFloat(respSum.toFixed(2)));
    if (totalBonus && !isNaN(totalBonus)) {
      setDiffToTotalBonus(parseFloat((totalBonus - issuedSum).toFixed(2)));
    } else {
      setDiffToTotalBonus(null);
    }
  }, [rows, totalBonus]);

  // 导入考核与奖励数据 Excel
  const loadInternalAssessItems = async () => {
    if(!month) return;
    setInternalAssessItemsLoading(true);
    try {
      const res = await api.get('/safety-points/internal-assess-items', { params: { month } });
      const list = res.data?.list || [];
      setInternalAssessItems(list);
      // 汇总：扣款/奖励项目合并，映射到主表“考核汇总”列展示
      const map = new Map();
      const sumMap = new Map();
      for (const it of list) {
        const empId = Number(it.employee_id);
        if (!empId) continue;
        const parts = map.get(empId) || [];
        if (it.deduct_item) parts.push(`扣：${it.deduct_item}`);
        if (it.reward_item) parts.push(`奖：${it.reward_item}`);
        map.set(empId, parts);

        const subtotal = Number(it.personal_subtotal) || 0;
        sumMap.set(empId, Number(((sumMap.get(empId) || 0) + subtotal).toFixed(2)));
      }
      setRows(prev => prev.map(r => {
        const parts = map.get(Number(r.employee_id)) || [];
        const uniq = Array.from(new Set(parts));
        const internalSum = sumMap.get(Number(r.employee_id));
        return {
          ...r,
          assessment_items_text: uniq.join('；'),
          // 有明细则用明细汇总回填(可正/负/0)，无明细不覆盖
          internal_assess_amount: internalSum === undefined ? r.internal_assess_amount : internalSum
        };
      }));
    } catch (e) {
      setInternalAssessItems([]);
      setRows(prev => prev.map(r => ({ ...r, assessment_items_text: '' })));
    } finally {
      setInternalAssessItemsLoading(false);
    }
  };

  useEffect(() => {
    if(!canView) return;
    loadInternalAssessItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, canView]);

  const openImportPreview = async (file) => {
    if(!file) return;
    if(!month) { message.error('先选择月份'); return; }
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    if(!isExcel){ message.error('仅支持Excel'); return; }
    setImportPreviewFile(file);
    setImportPreviewOpen(true);
    setImportPreviewLoading(true);
    setImportPreviewRows([]);
    setImportPreviewStats(null);
    setImportBindings({});
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/safety-points/import-assessments/preview?month=${month}`, formData, { headers:{ 'Content-Type':'multipart/form-data' } });
      const preview = res.data?.preview || [];
      setImportPreviewRows(preview);
      setImportPreviewStats(res.data?.stats || null);
      // 初始化 bindings：只为 matched 行记录其 employee_id；ambiguous 不默认绑定
      const initBindings = {};
      preview.forEach(r => {
        if (r?.match_status === 'matched' && r?.row_no && r?.employee_id) initBindings[r.row_no] = r.employee_id;
      });
      setImportBindings(initBindings);
      if (res.data?.format !== 'internal_assess_v1') {
        message.warning(res.data?.message || '未识别到预览表头，请检查Excel模板');
      }
    } catch (e) {
      message.error('预览失败: ' + (e.response?.data?.error || e.message));
      setImportPreviewOpen(false);
      setImportPreviewFile(null);
    } finally {
      setImportPreviewLoading(false);
    }
  };

  const doImport = async () => {
    if(!importPreviewFile) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importPreviewFile);
      formData.append('bindings', JSON.stringify(importBindings || {}));
      const res = await api.post(`/safety-points/import-assessments?month=${month}`, formData, { headers:{ 'Content-Type':'multipart/form-data' } });
      const imported = (res.data.records||[]).map(rec => ({
        employee_id: rec.employee_id,
        name: rows.find(r=>r.employee_id===rec.employee_id)?.name || '',
        department: rec.department || '',
        unit_name: rec.unit_name || '',
        position: rec.position || '',
        base_security_score: parseFloat(rec.base_security_score)||0,
        base_team_score: parseFloat(rec.base_team_score)||0,
        actual_security_score: parseFloat(rec.actual_security_score)||0,
        info_rate: parseFloat(rec.info_rate)||0,
        company_assess1_amount: parseFloat(rec.company_assess1_amount)||0,
        company_assess2_amount: parseFloat(rec.company_assess2_amount)||0,
        internal_assess_amount: parseFloat(rec.internal_assess_amount)||0,
        mobile_assess_amount: parseFloat(rec.mobile_assess_amount)||0,
        responsibility_bonus: parseFloat(rec.responsibility_bonus)||0,
        zero_injury_bonus: parseFloat(rec.zero_injury_bonus)||0,
        attendance_days: parseInt(rec.attendance_days)||0,
        final_safety_score: parseFloat(rec.final_safety_score)||null,
        composite_coeff: parseFloat(rec.composite_coeff)||null,
        issued_amount: parseFloat(rec.issued_amount)||null,
        assessment_amount: parseFloat(rec.assessment_amount)||null
      }));
      setRows(imported);
      await loadInternalAssessItems();
      message.success(`导入完成：更新 ${res.data.updated ?? '-'} 人，跳过 ${res.data.skipped ?? '-'} 行`);
      setImportPreviewOpen(false);
      setImportPreviewFile(null);
    } catch(e){
      message.error('导入失败: ' + (e.response?.data?.error || e.message));
    } finally {
      setImporting(false);
    }
  };

  const labelMap = {
    base_security_score:'安全治安消防基础分',
    base_team_score:'班组奖惩基础分',
    actual_security_score:'消防系数实际分值',
  info_rate:'信息化履职率',
    company_assess1_amount:'公司考核1金额',
    company_assess2_amount:'公司考核2金额',
    internal_assess_amount:'厂内部考核金额',
    mobile_assess_amount:'机动考评金额',
    responsibility_bonus:'责任制奖金',
    zero_injury_bonus:'百日零伤害奖励',
    attendance_days:'出勤天数'
  };
  // 列顺序：基础分 -> 乘积(消防系数实际分值) -> 履职率 -> 安全分 -> 安全奖综合系数 -> 各考核奖金 -> 出勤 -> 核发/汇总
  const baseOrder = [
    'base_security_score','base_team_score','actual_security_score','info_rate'
  ];
  const amountOrder = [
    'company_assess1_amount','company_assess2_amount','internal_assess_amount','mobile_assess_amount','responsibility_bonus','zero_injury_bonus','attendance_days'
  ];
  const readOnlyKeys = new Set(['actual_security_score','final_safety_score','composite_coeff','responsibility_bonus','issued_amount','assessment_amount']);
  const columns = [
    { title:'姓名', dataIndex:'name', fixed:'left', width:110 },
    { title:'部门', dataIndex:'department', width:120 },
    { title:'科室/单位', dataIndex:'unit_name', width:120 },
    { title:'岗位', dataIndex:'position', width:120 },
    ...baseOrder.map(key => ({
      title: labelMap[key],
      dataIndex: key,
      width: key==='actual_security_score'?150:130,
      render: (v, record) => {
        if(!canManage || key==='actual_security_score') return v==null?'-':v;
        const max = undefined; // 不限制上限
        const step = 0.01;
        return (
          <InputNumber
            value={v}
            size="small"
            min={0}
            max={max}
            step={step}
            onChange={val => updateRow(record.employee_id, key, val==null?0:val)}
            style={{ width:'100%' }}
          />
        );
      }
    })),
    {
      title:'加减分',
      dataIndex:'adjustment_points',
      width:120,
      render: (v, record) => {
        const num = Number(v) || 0;
        const tag = <Tag color={num > 0 ? 'green' : (num < 0 ? 'red' : 'default')}>{num > 0 ? `+${num.toFixed(2)}` : num.toFixed(2)}</Tag>;
        if (!canManage) return tag;
        return (
          <Space size={6}>
            {tag}
            <Button size="small" type="link" onClick={() => openAdjustModal(record)}>加减分</Button>
          </Space>
        );
      }
    },
    { title:'安全分', dataIndex:'final_safety_score', width:130, render: v => v==null?'-':Number(v).toFixed(2) },
    { title:'安全奖综合系数', dataIndex:'composite_coeff', width:150, render: v => v==null?'-':Number(v).toFixed(4) },
    ...amountOrder.map(key => ({
      title: labelMap[key],
      dataIndex: key,
      width: key==='internal_assess_amount'?160:130,
      render: (v, record) => {
        if(!canManage || readOnlyKeys.has(key)) return v==null?'-':v;
        return (
          <InputNumber
            value={v}
            size="small"
            min={key==='internal_assess_amount' ? undefined : 0}
            onChange={val => updateRow(record.employee_id, key, val==null?0:val)}
            style={{ width:'100%' }}
          />
        );
      }
    })),
    { title:'核发金额', dataIndex:'issued_amount', width:130, render: v => v==null?'-':Number(v).toFixed(2) },
    {
      title:'考核汇总',
      dataIndex:'assessment_amount',
      width:260,
      render: (v, record) => {
        const text = record?.assessment_items_text;
        const amount = v==null?null:Number(v);
        if (!text) return amount==null?'-':amount.toFixed(2);
        return (
          <div style={{ lineHeight: 1.2 }}>
            <Typography.Text ellipsis={{ tooltip: text }} style={{ display: 'block', maxWidth: 240 }}>
              {text}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              金额：{amount==null?'-':amount.toFixed(2)}
            </Typography.Text>
          </div>
        );
      }
    }
  ];

  const filteredRows = useMemo(() => {
    if (!canView) return [];
    const q = String(nameQuery || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => String(r.name || '').toLowerCase().includes(q));
  }, [rows, nameQuery, canView]);

  if (!canView) return <Alert type="warning" message="无权限查看安全积分" showIcon />;

  const importPreviewColumns = [
    { title:'序号', dataIndex:'seq_no', width:70, render: v => v==null?'-':v },
    { title:'姓名', dataIndex:'name', width:110 },
    { title:'扣款项目', dataIndex:'deduct_item', width:160 },
    { title:'扣款（元）', dataIndex:'deduct_amount', width:100, render: v => v==null?'-':Number(v).toFixed(2) },
    { title:'扣款类型', dataIndex:'deduct_type', width:120 },
    { title:'奖励项目', dataIndex:'reward_item', width:160 },
    { title:'奖励（元）', dataIndex:'reward_amount', width:100, render: v => v==null?'-':Number(v).toFixed(2) },
    { title:'奖励类型', dataIndex:'reward_type', width:120 },
    { title:'上月累计项目', dataIndex:'last_month_cumulative_item', width:160 },
    { title:'个人小计', dataIndex:'personal_subtotal', width:100, render: v => {
      const n = Number(v) || 0;
      return <Tag color={n>0?'green':(n<0?'red':'default')}>{n>0?`+${n.toFixed(2)}`:n.toFixed(2)}</Tag>;
    }},
    {
      title:'绑定员工',
      dataIndex:'employee_id',
      width:220,
      render: (_, record) => {
        const status = record?.match_status;
        if (status === 'matched') {
          const first = record?.candidates?.[0];
          const label = first ? `${first.department||''}/${first.unit_name||''}/${first.position||''}`.replace(/^\/+|\/+$/g,'') : '';
          return <Tag color="green">已匹配{label?`：${label}`:''}</Tag>;
        }
        if (status === 'ambiguous') {
          const opts = (record?.candidates || []).map(c => ({
            value: c.id,
            label: `${c.department||''}/${c.unit_name||''}/${c.position||''}`.replace(/^\/+|\/+$/g,'') || `员工ID ${c.id}`
          }));
          return (
            <Select
              placeholder="请选择绑定员工"
              size="small"
              style={{ width: '100%' }}
              options={opts}
              value={importBindings?.[record.row_no] || undefined}
              onChange={(val) => setImportBindings(prev => ({ ...prev, [record.row_no]: val }))}
            />
          );
        }
        return <Tag color="red">未匹配</Tag>;
      }
    },
    { title:'匹配', dataIndex:'match_status', width:90, fixed:'right', render: (v) => {
      if(v==='matched') return <Tag color="green">已匹配</Tag>;
      if(v==='ambiguous') return <Tag color="orange">重名</Tag>;
      return <Tag color="red">未匹配</Tag>;
    }}
  ];

  const internalAssessItemsColumns = [
    { title:'序号', dataIndex:'seq_no', width:70, render: v => v==null?'-':v },
    { title:'姓名', dataIndex:'name', width:110 },
    { title:'扣款项目', dataIndex:'deduct_item', width:160 },
    { title:'扣款（元）', dataIndex:'deduct_amount', width:100, render: v => v==null?'-':Number(v).toFixed(2) },
    { title:'扣款类型', dataIndex:'deduct_type', width:120 },
    { title:'奖励项目', dataIndex:'reward_item', width:160 },
    { title:'奖励（元）', dataIndex:'reward_amount', width:100, render: v => v==null?'-':Number(v).toFixed(2) },
    { title:'奖励类型', dataIndex:'reward_type', width:120 },
    { title:'上月累计项目', dataIndex:'last_month_cumulative_item', width:160 },
    { title:'个人小计', dataIndex:'personal_subtotal', width:100, render: v => {
      const n = Number(v) || 0;
      return <Tag color={n>0?'green':(n<0?'red':'default')}>{n>0?`+${n.toFixed(2)}`:n.toFixed(2)}</Tag>;
    }}
  ];

  const hasUnresolvedAmbiguous = (importPreviewRows || []).some(
    r => r?.match_status === 'ambiguous' && r?.row_no && !importBindings?.[r.row_no]
  );

  return (
    <Card title="安全积分汇算">
      <Space direction="vertical" style={{ width:'100%' }} size="middle">
        <Row gutter={16}>
          <Col span={5}>
            <DatePicker picker="month" value={dayjs(month+'-01')} onChange={d=> d && setMonth(d.format('YYYY-MM'))} style={{ width:'100%' }} />
          </Col>
          <Col span={5}>
            <InputNumber
              placeholder="奖金总额(记录)"
              value={totalBonus}
              onChange={v=> setTotalBonus(v)}
              style={{ width:'100%' }}
              min={0}
              precision={2}
              disabled={!canManage}
            />
          </Col>
          <Col span={5}>
            <InputNumber
              placeholder="基础系数"
              value={baseMultiplier}
              onChange={v=> setBaseMultiplier(v)}
              style={{ width:'100%' }}
              min={0}
              precision={4}
              disabled={!canManage}
            />
          </Col>
          <Col span={9} style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            <Button onClick={loadSavedRecords} loading={loadingRecords}>加载已保存</Button>
            <Button onClick={resetToEmployeeBase} disabled={loadingEmployees}>重置</Button>
            {canManage && <Button type="primary" onClick={performCalculation} loading={saving}>计算并保存</Button>}
            <Button onClick={exportCalcExcel}>导出汇算结果</Button>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Input
              placeholder="按姓名检索"
              value={nameQuery}
              allowClear
              onChange={e => setNameQuery(e.target.value)}
            />
          </Col>
          <Col span={16} />
        </Row>
        {canManage && (
          showFormula ? (
            <Card size="small" title="公式设置" extra={<Button size="small" onClick={()=>setShowFormula(false)}>折叠</Button>} style={{ background:'#fafafa' }}>
              <Typography.Paragraph style={{ marginBottom:8 }}>
                当前公式：
                <br />消防系数实际分值 = 安全治安消防基础分 × 班组奖惩基础分
                <br />安全分 = 信息化履职率 × 100
                <br />安全奖综合系数 = 消防系数实际分值 × (安全分 + 公司考核1金额)
                <br />责任制奖金 = 安全奖综合系数 × 基础系数 + 公司考核2金额 + 厂内部考核金额 + 机动考评金额
                <br />核发金额 = 责任制奖金 + 百日零伤害奖励
                <br />试用期 (&lt;3个月) 基础分固定 2.4；岗位基础分：项目经理=9，副经理=7，安全员=7，其它=3；班组奖惩基础分默认 1
              </Typography.Paragraph>
              <Space wrap>
                <Upload.Dragger
                  name="file"
                    customRequest={({ file, onSuccess }) => { openImportPreview(file); onSuccess && onSuccess('ok'); }}
                  multiple={false}
                  showUploadList={false}
                    disabled={importing || importPreviewLoading}
                  style={{ width:340 }}
                >
                  <p className="ant-upload-drag-icon"><UploadOutlined /></p>
                  <p className="ant-upload-text">导入考核/奖励Excel</p>
                </Upload.Dragger>
              </Space>
            </Card>
          ) : (
            <Card size="small" bodyStyle={{ padding:8 }} style={{ background:'#fafafa' }}>
              <Space align="center" style={{ width:'100%', justifyContent:'space-between' }}>
                <span style={{ fontSize:12, color:'#555' }}>公式设置 (已折叠)</span>
                <Space>
                  <Button size="small" onClick={()=>setShowFormula(true)}>展开公式</Button>
                  <Upload.Dragger
                    name="file"
                    customRequest={({ file, onSuccess }) => { openImportPreview(file); onSuccess && onSuccess('ok'); }}
                    multiple={false}
                    showUploadList={false}
                    disabled={importing || importPreviewLoading}
                    style={{ width:150, padding:4 }}
                  >
                    <p style={{ margin:0, fontSize:12 }}>导入Excel</p>
                  </Upload.Dragger>
                </Space>
              </Space>
            </Card>
          )
        )}
        <Card size="small" bodyStyle={{ padding:8 }} style={{ background:'#fefefe' }}>
          <Row gutter={16}>
            <Col span={6}><Statistic title="核发金额总额" value={issuedTotal} precision={2} /></Col>
            <Col span={6}><Statistic title="责任制奖金总额" value={responsibilityTotal} precision={2} /></Col>
            <Col span={6}><Statistic title="录入奖金总额" value={totalBonus || 0} precision={2} /></Col>
            <Col span={6}><Statistic title="录入总额差额(录入-核发)" value={diffToTotalBonus==null?'-':diffToTotalBonus} precision={2} /></Col>
          </Row>
        </Card>
        {calcMeta && (
          <Row gutter={16}>
            <Col span={6}><Statistic title="综合系数总计" value={compositeTotal.toFixed(4)} /></Col>
            <Col span={6}><Statistic title="基础系数" value={calcMeta.base_multiplier} precision={4} /></Col>
          </Row>
        )}
        <Table
          size="small"
          dataSource={filteredRows}
          columns={columns}
          rowKey="employee_id"
          scroll={{ x: 2400, y: 500 }}
          pagination={{ pageSize: 20 }}
        />

        <Card
          size="small"
          title="导入明细（厂内部考核/奖励）"
          extra={(
            <Space>
              <Button size="small" onClick={exportInternalAssessExcel} disabled={!internalAssessItems?.length}>导出明细</Button>
              <Button size="small" onClick={loadInternalAssessItems} loading={internalAssessItemsLoading}>刷新</Button>
            </Space>
          )}
          bodyStyle={{ padding: 8 }}
        >
          <Table
            size="small"
            loading={internalAssessItemsLoading}
            dataSource={internalAssessItems}
            rowKey="id"
            columns={internalAssessItemsColumns}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1400 }}
          />
        </Card>

        <Modal
          open={importPreviewOpen}
          title={`导入预览（${month}）`}
          onCancel={() => { if(!importing){ setImportPreviewOpen(false); setImportPreviewFile(null); } }}
          onOk={doImport}
          okText="确认导入"
          cancelText="取消"
          confirmLoading={importing}
          okButtonProps={{ disabled: importPreviewLoading || !importPreviewFile || hasUnresolvedAmbiguous }}
          destroyOnClose
          width={1150}
        >
          {importPreviewStats && (
            <Alert
              type="info"
              showIcon
              message={`共 ${importPreviewStats.total} 行，空行跳过 ${importPreviewStats.skipped}，已匹配 ${importPreviewStats.matched}，重名 ${importPreviewStats.ambiguous}，未匹配 ${importPreviewStats.unmatched}`}
              style={{ marginBottom: 8 }}
            />
          )}
          {hasUnresolvedAmbiguous && (
            <Alert
              type="warning"
              showIcon
              message="存在“重名”行未选择绑定员工，请先在‘绑定员工’列选择后再导入。"
              style={{ marginBottom: 8 }}
            />
          )}
          <Table
            size="small"
            loading={importPreviewLoading}
            dataSource={importPreviewRows}
            rowKey={(r, idx) => `${r.employee_id||'na'}_${r.seq_no||idx}_${idx}`}
            columns={importPreviewColumns}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1600, y: 420 }}
          />
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 8 }}
            message="说明：导入后会按‘个人小计’汇总写入‘厂内部考核金额’，可为正数/负数/0；如存在重名员工，需在弹窗中手动选择绑定对象后才能导入。"
          />
        </Modal>

        <Modal
          open={adjustOpen}
          title={adjustEmployee ? `加减分 - ${adjustEmployee.name}` : '加减分'}
          onCancel={() => { setAdjustOpen(false); setAdjustEmployee(null); setAdjustRecords([]); }}
          onOk={submitAdjustment}
          okText="保存"
          cancelText="关闭"
          confirmLoading={adjustSaving}
          destroyOnClose
          width={820}
        >
          <Space wrap style={{ marginBottom: 8 }}>
            <span style={{ color: '#666' }}>快捷操作：</span>
            <Button
              size="small"
              onClick={() => {
                adjustForm.setFieldsValue({ category: 'violation', action: 'deduct' });
              }}
            >
              违章行为（减分）
            </Button>
            <Button
              size="small"
              onClick={() => {
                adjustForm.setFieldsValue({ category: 'contribution', action: 'add' });
              }}
            >
              安全贡献积分（加分）
            </Button>
          </Space>

          <Form
            form={adjustForm}
            layout="vertical"
            onValuesChange={(changedValues) => {
              if (Object.prototype.hasOwnProperty.call(changedValues, 'category')) {
                const cat = changedValues.category;
                if (cat === 'violation') adjustForm.setFieldsValue({ action: 'deduct' });
                if (cat === 'contribution') adjustForm.setFieldsValue({ action: 'add' });
              }
            }}
          >
            <Row gutter={16}>
              <Col xs={24} md={6}>
                <Form.Item name="action" label="动作" rules={[{ required:true }]}>
                  <Select options={[{ value:'add', label:'加分' }, { value:'deduct', label:'减分' }]} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="category" label="类型" rules={[{ required:true }]}>
                  <Select options={[
                    { value:'violation', label:'违章行为' },
                    { value:'contribution', label:'安全贡献积分' },
                    { value:'behavior', label:'行为(其它)' },
                    { value:'safety_education', label:'安全教育' },
                    { value:'other', label:'其他' }
                  ]} />
                </Form.Item>
              </Col>
              <Col xs={24} md={5}>
                <Form.Item name="points" label="分值" rules={[{ required:true, message:'请输入分值' }]}>
                  <InputNumber min={0.01} step={0.01} style={{ width:'100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={5}>
                <Form.Item name="occurred_date" label="日期" rules={[{ required:true, message:'请选择日期' }]}>
                  <DatePicker style={{ width:'100%' }} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="description" label="说明">
                  <Input placeholder="例如：安全教育培训合格 / 现场违章" />
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <Table
            size="small"
            loading={adjustLoading}
            dataSource={adjustRecords}
            rowKey="id"
            pagination={{ pageSize: 6 }}
            columns={[
              { title:'日期', dataIndex:'occurred_date', width:110 },
              { title:'类型', dataIndex:'category', width:140, render: v => ({ violation:'违章行为', contribution:'安全贡献积分', behavior:'行为(其它)', safety_education:'安全教育', other:'其他' }[v] || v) },
              { title:'加减分', dataIndex:'points_delta', width:110, render: v => {
                const n = Number(v) || 0;
                return <Tag color={n > 0 ? 'green' : (n < 0 ? 'red' : 'default')}>{n > 0 ? `+${n.toFixed(2)}` : n.toFixed(2)}</Tag>;
              }},
              { title:'说明', dataIndex:'description', render: v => v || '-' },
              { title:'录入人', dataIndex:'created_by_user_name', width:120, render: v => v || '-' },
              { title:'时间', dataIndex:'created_at', width:160, render: v => v || '-' },
              {
                title:'操作',
                width:90,
                render: (_, r) => canManage ? (
                  <Popconfirm title="确认删除这条加减分记录？" onConfirm={() => deleteAdjustment(r.id)}>
                    <Button size="small" danger>删除</Button>
                  </Popconfirm>
                ) : '-'
              }
            ]}
          />
          <div style={{ fontSize:12, color:'#666' }}>
            提示：加减分将影响该月安全分，建议保存后重新“计算并保存”。
          </div>
        </Modal>
      </Space>
    </Card>
  );
};

export default SafetyPointsCalcPage;
